import * as React from "react"
import {useState, useEffect, useRef} from 'react'
import { graphql } from "gatsby"
import { Helmet } from "react-helmet"

import { debounce } from '../utilities/helpers'

import Layout from "../components/layout"
import Seo from "../components/seo"
import Header from "../components/Header/Header"
import Hero from "../components/Hero/Hero"
import ChapterOne from "../components/ChapterOne/ChapterOne"
import ChapterTwo from "../components/ChapterTwo/ChapterTwo"
import ChapterThree from "../components/ChapterThree/ChapterThree"
import Footer from "../components/Footer/Footer"
import Footnotes from "../components/Footnotes/Footnotes"

const IndexPage = ({data}) => {
  const frontmatter = data.allMarkdownRemark.edges[0].node.frontmatter;

  const [footnoteActive, setfootnoteActive] = useState(false);
  const [fixedElVisible, setfixedElVisible] = useState(false);
  const [prevScrollPos, setPrevScrollPos] = useState(0);
  const [navigationActive, setNavigationActive] = useState(false);

  // Get height of hero component 
  const heroRef = useRef();
  const heroHeight = heroRef.current?.clientHeight;

  // Toggle function for footnote slide up
  const toggleFootnote = () => {
    footnoteActive === true ? setfootnoteActive(false) : setfootnoteActive(true);
  }

  // Function to handle when navigation, and fixed position elements slide in
  // Wrapped in debounce function to limit how many times it's fired.
  const handleScroll = debounce(() => {
    const currentScrollPos = window.pageYOffset;

    // Navigation logic. Checks if user has scrolled up more than 70px, and is currently below hero
    setNavigationActive(prevScrollPos > currentScrollPos && prevScrollPos - currentScrollPos > 70 && currentScrollPos > heroHeight);

    // Logic for fixed position elements
    if (currentScrollPos > heroHeight) {
      setfixedElVisible(true);
    } else {
      setfixedElVisible(false);
      setfootnoteActive(false);
    }

    setPrevScrollPos(currentScrollPos);

  }, 100);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);

  }, [prevScrollPos, navigationActive, fixedElVisible, footnoteActive, handleScroll]);

  return(
    <Layout>
      <Helmet> 
      <meta http-equiv = "refresh" content = "0; url = https://linktr.ee/creator-report/" />
      </Helmet>
      <Seo title="Home" />
      <Header
        fixedElVisible={fixedElVisible} 
        navigationActive={navigationActive}
      />
      <div ref={heroRef}>
        <Hero content={frontmatter.hero}/>
      </div>
      <ChapterOne content={frontmatter.chapterOne} toggleFootnote={toggleFootnote}/>
      <ChapterTwo content={frontmatter.chapterTwo} toggleFootnote={toggleFootnote}/>
      <ChapterThree content={frontmatter.chapterThree} toggleFootnote={toggleFootnote}/>
      <Footer content={frontmatter.footer}/>
      <Footnotes 
        footnoteActive={footnoteActive} 
        setfootnoteActive={setfootnoteActive} 
        fixedElVisible={fixedElVisible} 
        toggleFootnote={toggleFootnote}/>
    </Layout>
  )
}

export const pageQuery = graphql`
  query HomePageQuery {
    allMarkdownRemark(filter: {frontmatter: {title: {eq: "Page"}}}) {
      edges {
        node {
          frontmatter {
            hero {
              headline
              paragraph
              subhead
            }
            chapterOne {
              headline
              paragraph
              section1_headline
              section1_subhead
              section1_paragraph
              section1_legend {
                item {
                  paragraph
                  title
                }
              }
              section2_headline
              section2_subhead
              section2_chart {
                item {
                  description
                  title
                  value
                  image {
                    publicURL
                  }
                }
              }
              section2_quotes {
                items {
                  creator
                  followers
                  link
                  quote
                  avatar {
                    childImageSharp {
                      gatsbyImageData (
                        quality: 100
                        layout: FULL_WIDTH
                        placeholder: BLURRED
                        formats: [AUTO, WEBP]
                      )
                    }
                  }
                }
              }
            }
            chapterTwo {
              headline
              paragraph
              section1_tables {
                table {
                  headline
                  subhead
                  paragraph
                  image {
                    childImageSharp {
                      gatsbyImageData (
                        quality: 100
                        layout: FULL_WIDTH
                        placeholder: BLURRED
                        formats: [AUTO, WEBP]
                      )
                    }
                  }
                  mobileImage {
                    childImageSharp {
                      gatsbyImageData (
                        quality: 100
                        layout: FULL_WIDTH
                        placeholder: BLURRED
                        formats: [AUTO, WEBP]
                      )
                    }
                  }
                  item {
                    description
                    statistic
                  }
                }
              }
              section2_chart {
                headline
                subhead
                legend {
                  color
                  value
                }
                chartBox {
                  statistic
                  paragraph
                  color
                }
              }
              section2_tables {
                table {
                  headline
                  item {
                    description
                    statistic
                  }
                }
              }
              quotes {
                items {
                  quote
                  link
                  creator
                  followers
                  avatar {
                    childImageSharp {
                      gatsbyImageData (
                        quality: 100
                        layout: FULL_WIDTH
                        placeholder: BLURRED
                        formats: [AUTO, WEBP]
                      )
                    }
                  }
                }
              }
              section3_headline
              section3_subhead
              section3_chart {
                publicURL
              }
            }
            chapterThree {
              headline
              paragraph
              tipOne {
                headline
                paragraph
              }
              pieChartsOne {
                footnote
                paragraph
                stat
                image {
                  publicURL
                }
              }
              tipTwo {
                headline
                paragraph
              }
              pieChartsTwo {
                paragraph
                stat
                image {
                  publicURL
                }
              }
              impactTable {
                eyebrow
                headline
                tableColumn {
                  title
                }
                tableRows {
                  statA
                  statB
                  title
                }
              }
              tipThree {
                headline
                paragraph
              }
              biggestPlatform {
                text {
                  stat
                  paragraph
                }
                image {
                  publicURL
                }
              }
              contentFormats {
                headline
                paragraph
                chart {
                  publicURL
                }
              }
              quotes {
                items {
                  quote
                  link
                  creator
                  followers
                  avatar {
                    childImageSharp {
                      gatsbyImageData (
                        quality: 100
                        layout: FULL_WIDTH
                        placeholder: BLURRED
                        formats: [AUTO, WEBP]
                      )
                    }
                  }
                }
              }
            }
            footer {
              headline
              paragraph
              subhead
              image {
                childImageSharp {
                  gatsbyImageData (
                    quality: 100
                    layout: FULL_WIDTH
                    placeholder: BLURRED
                    formats: [AUTO, WEBP]
                  )
                }
              }
            }
          }
        }
      }
    }
  }
`

export default IndexPage
